import {
  UnblockButton,
  InputStepHoverCard,
  InputStepUser,
} from "~/components/Playground/Node/InputStepNode";
import {
  BlockStep,
  InputStep,
  Outcome,
  State,
  hasReceivedInput,
  isInputDisabled,
} from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { twMerge } from "tailwind-merge";
import Emojify from "~/components/shared/Emojify";
import classNames from "classnames";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { StepLink } from "~/components/build/Show/components/Sidebar/TreeNode/StepLink";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import { TreeNode, TreeNodeType } from "~/components/build/Show/components/Sidebar/TreeNode";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";

interface Props {
  step: InputStep | BlockStep;
  depth?: number;
}

export function InputStepItem({ step, depth = 0 }: Props) {
  const { currentStep } = useCurrentJobOrStep();
  const { missingDependencies } = useStepDependencies(step.uuid);

  const url = resolveStepUrl(step.uuid);
  const label = step.label || "Continue";

  if (hasReceivedInput(step)) {
    return (
      <HoverCard.Root>
        <HoverCard.Portal>
          <InputStepHoverCard step={step} className="w-fit" collisionPadding={5} side="right" />
        </HoverCard.Portal>

        <HoverCard.Trigger asChild={true}>
          <div className="w-full">
            <StepLink
              state={step.state}
              to={url}
              active={step.uuid === currentStep?.uuid}
              depth={depth}
            >
              <div className="flex min-w-0 flex-auto py-1">
                <div className="w-full max-w-full truncate font-medium">
                  <Emojify text={label} />
                </div>
              </div>

              <span className="flex w-5 shrink-0 items-center justify-center self-stretch">
                <InputStepUser step={step} />
              </span>

              <div className="flex shrink-0 items-center justify-center self-stretch">
                <StateIcon
                  state={State.Finished}
                  outcome={Outcome.Passed}
                  missingDependencies={missingDependencies}
                  className={twMerge(
                    "-mr-0.5",
                    textColorForStep({
                      state: State.Finished,
                      outcome: Outcome.Passed,
                      missingDependencies,
                    }),
                  )}
                />
              </div>
            </StepLink>
          </div>
        </HoverCard.Trigger>
      </HoverCard.Root>
    );
  }

  if (isInputDisabled(step)) {
    return (
      <StepLink
        to={url}
        active={step.uuid === currentStep?.uuid}
        depth={depth}
        className="cursor-not-allowed"
        disabled={true}
      >
        <div className="relative flex min-w-0 flex-auto items-baseline justify-start text-left font-medium opacity-50">
          <div className="flex min-w-0 flex-auto py-1">
            <div className="w-full max-w-full truncate leading-normal">
              <Emojify text={label} />
            </div>
          </div>

          <span className="flex shrink-0 items-center justify-center self-stretch">
            <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs font-medium">
              Input required
            </span>
          </span>
        </div>
      </StepLink>
    );
  }

  return (
    <UnblockButton
      step={step}
      className={classNames("unstyled-button hover:bg-purple-100/50-white w-full rounded-md", {
        "pl-2": depth === 0,
        "pl-[calc(1.5rem_+_2px)]": depth === 1,
      })}
    >
      <div className="relative flex min-w-0 flex-auto items-baseline justify-start text-left font-medium">
        <div className="flex min-w-0 flex-auto py-1">
          <div className="w-full max-w-full truncate leading-normal">
            <Emojify text={label} />
          </div>
        </div>

        <span className="flex shrink-0 items-center justify-center gap-1 self-stretch">
          <span className="rounded-sm bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-600">
            Input required
          </span>
          <StateIcon
            state={step.state}
            outcome={step.outcome}
            missingDependencies={missingDependencies}
            className={twMerge("mr-1.5", textColorForStep({ ...step, missingDependencies }))}
          />
        </span>
      </div>
    </UnblockButton>
  );
}

export const createInputStepNode = (step: InputStep | BlockStep): TreeNode => ({
  id: step.uuid,
  type: TreeNodeType.StepInput,
  render: (props) => <InputStepItem step={step} {...props} />,
});
